'use client';
/* eslint-disable tailwindcss/no-arbitrary-value -- the tooltip layer and shadow are the prototype's tokens.
   The layer is `!important`: tailwind-merge cannot tell it conflicts with the
   base card's `z-30`, so without it the CSS order would pick the winner. */
import { InlineTooltip } from '../../../components/InlineTooltip';
import { LoanRewardPill } from '../../../components/LoanRewardPill';
import { useCmsClientContext } from '../../../utils/cmsPageContext';
import {
  formatLoanRewardAmount,
  LoanRewardBreakdown,
} from '../../../utils/helpers/personalLoanReward';
import { getClientTranslations } from '../../../utils/translations/getClientTranslations';
import { TRANSLATIONS } from '../i18n';

type LoanRewardCellProps = {
  breakdown: LoanRewardBreakdown;
  /** Hover from 1280px, tap below it — the CMS-1790 attribute-tooltip split. */
  openOn: 'click' | 'hover';
};

/**
 * Total Reward in a Personal Loan Quick Answer (CMS-1928 R4): the "Up to" pill,
 * with the per-source breakdown in a tooltip instead of in the cell. The pill is
 * not a modal entry point. The tooltip is the attribute tooltip's own, so the two
 * open and close the same way.
 */
export const LoanRewardCell = ({ breakdown, openOn }: LoanRewardCellProps) => {
  const context = useCmsClientContext();
  const staticText = getClientTranslations(TRANSLATIONS, context);

  const money = (value: number) =>
    formatLoanRewardAmount(value, breakdown, context.currencyDisplay);

  // Exclusive first, provider second; a source with nothing qualifying is
  // omitted, and no value carries the pill's "Up to".
  const sources = [
    { label: staticText.reward_source_exclusive, value: breakdown.exclusive },
    { label: staticText.reward_source_provider, value: breakdown.provider },
  ].filter(
    (source): source is { label: string; value: number } =>
      source.value !== undefined,
  );

  return (
    <InlineTooltip
      placement="top"
      onOpenEvent={openOn}
      tooltipClassName="!z-[var(--z-index-modal-tooltip)] min-w-30 bg-fill/card shadow-[0_1px_4px_0_rgba(51,51,51,0.16)] md:min-w-30"
      infoClassName={openOn === 'click' ? 'cursor-pointer' : undefined}
      trigger={
        <LoanRewardPill value={money(breakdown.total)} size="cell-primary" />
      }
      content={
        <div className="flex flex-col gap-spacing/gap/soft-12px">
          {sources.map((source) => (
            <div key={source.label} className="flex flex-col gap-0.5">
              <span className="font-font-weight/caption text-font-size/caption leading-line-height/caption text-text/secondary-lighter">
                {source.label}
              </span>
              <span className="font-font-weight/table-cell-strong text-font-size/table-cell leading-line-height/table-cell text-text/primary">
                {money(source.value)}
              </span>
            </div>
          ))}
        </div>
      }
    />
  );
};
